import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Coins,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  IndianRupee,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { CREDIT_PLAN_CATALOG } from '@/lib/billing/creditPlans';

const PLAN_LABELS = Object.fromEntries(
  CREDIT_PLAN_CATALOG.filter((p) => !p.custom).map((p) => [p.id, p.name]),
);

const EMPTY_PLAN_FORM = {
  original_price_inr: '',
  offer_price_inr: '',
  credits: '',
  active: true,
};

const EMPTY_FORM = {
  id: '',
  media_type: 'image',
  provider: 'nano_banana',
  mode: 'default',
  label: '',
  credits_flat: '0',
  credits_per_unit: '0',
  duration_unit_seconds: '0',
  active: true,
};

const MEDIA_TYPES = [
  { value: 'image', label: 'Image generation' },
  { value: 'video', label: 'Video generation' },
  { value: 'audio', label: 'Audio / TTS' },
  { value: 'music', label: 'Background music' },
];

const PROVIDER_HINTS = {
  image: ['nano_banana', 'minimax', 'runway', 'veo3', 'inhouse', 'default'],
  video: ['minimax', 'runway', 'veo3', 'ltx', 'nano_banana', 'narration_slideshow', 'seedance', 'seedance_2_5', 'chatgpt_astra', 'fable_5_1', 'inhouse', 'default'],
  audio: ['edge_tts', 'elevenlabs', 'murf', 'minimax', 'runway', 'auto', 'default'],
  music: ['minimax', 'default'],
};

function formatInr(credits, cpi) {
  const c = Number(credits) || 0;
  const rate = Number(cpi) || 10;
  if (!c) return '—';
  return `≈ ₹${(c / rate).toFixed(2)}`;
}

function rateSummary(row) {
  const flat = Number(row.credits_flat) || 0;
  const per = Number(row.credits_per_unit) || 0;
  const unit = Number(row.duration_unit_seconds) || 0;
  if (per > 0 && unit > 0) return `${per} cr / ${unit}s`;
  if (flat > 0) return `${flat} cr flat`;
  return '0 cr';
}

export function AdminBillingPanel({ embedded = false }) {
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState({ credits_per_inr: '10', billing_enabled: true });
  const [savingSettings, setSavingSettings] = useState(false);

  const [creditPlans, setCreditPlans] = useState([]);
  const [planDrafts, setPlanDrafts] = useState({});
  const [savingPlanId, setSavingPlanId] = useState(null);

  const [rates, setRates] = useState([]);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaFilter, setMediaFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, ratesRes, usageRes, plansRes] = await Promise.all([
        api.get('/admin/billing/settings'),
        api.get('/admin/billing/rates'),
        api.get('/admin/billing/usage-summary'),
        api.get('/admin/billing/plans'),
      ]);
      const s = settingsRes.data || {};
      setSettings(s);
      setSettingsDraft({
        credits_per_inr: String(s.credits_per_inr ?? 10),
        billing_enabled: s.billing_enabled !== false,
      });
      const plans = Array.isArray(plansRes.data?.items) ? plansRes.data.items : [];
      setCreditPlans(plans);
      const drafts = {};
      for (const plan of plans) {
        drafts[plan.id] = {
          original_price_inr: String(plan.original_price_inr ?? ''),
          offer_price_inr: String(plan.offer_price_inr ?? ''),
          credits: String(plan.credits ?? ''),
          active: plan.active !== false,
        };
      }
      setPlanDrafts(drafts);
      setRates(Array.isArray(ratesRes.data?.items) ? ratesRes.data.items : []);
      setUsage(Array.isArray(usageRes.data?.items) ? usageRes.data.items : []);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load billing data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const groupedRates = useMemo(() => {
    const filtered =
      mediaFilter === 'all'
        ? rates
        : rates.filter((r) => r.media_type === mediaFilter);
    const groups = {};
    for (const row of filtered) {
      const key = row.media_type || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    }
    return MEDIA_TYPES.filter((m) => groups[m.value]?.length)
      .map((m) => ({ ...m, rows: groups[m.value] || [] }));
  }, [rates, mediaFilter]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put('/admin/billing/settings', {
        credits_per_inr: parseFloat(settingsDraft.credits_per_inr) || 10,
        billing_enabled: settingsDraft.billing_enabled,
      });
      setSettings(data);
      toast.success('Global pricing updated');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const setPlanHalfOffer = (planId) => {
    setPlanDrafts((prev) => {
      const draft = prev[planId];
      if (!draft) return prev;
      const original = parseFloat(draft.original_price_inr) || 0;
      return {
        ...prev,
        [planId]: {
          ...draft,
          offer_price_inr: original > 0 ? String(Math.round(original * 50) / 100) : draft.offer_price_inr,
        },
      };
    });
  };

  const saveCreditPlan = async (planId) => {
    const draft = planDrafts[planId];
    if (!draft) return;
    setSavingPlanId(planId);
    try {
      const { data } = await api.put(`/admin/billing/plans/${planId}`, {
        original_price_inr: parseFloat(draft.original_price_inr) || 0,
        offer_price_inr: parseFloat(draft.offer_price_inr) || 0,
        credits: parseFloat(draft.credits) || 0,
        active: draft.active,
      });
      setCreditPlans((rows) => rows.map((r) => (r.id === planId ? data : r)));
      setPlanDrafts((prev) => ({
        ...prev,
        [planId]: {
          original_price_inr: String(data.original_price_inr ?? ''),
          offer_price_inr: String(data.offer_price_inr ?? ''),
          credits: String(data.credits ?? ''),
          active: data.active !== false,
        },
      }));
      toast.success(`${PLAN_LABELS[planId] || planId} plan updated`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save plan'));
    } finally {
      setSavingPlanId(null);
    }
  };

  const openCreate = (mediaType = 'image') => {
    const prov = PROVIDER_HINTS[mediaType]?.[0] || 'default';
    setForm({ ...EMPTY_FORM, id: '', media_type: mediaType, provider: prov });
    setPreview(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id || '',
      media_type: row.media_type || 'image',
      provider: row.provider || 'default',
      mode: row.mode || 'default',
      label: row.label || '',
      credits_flat: String(row.credits_flat ?? 0),
      credits_per_unit: String(row.credits_per_unit ?? 0),
      duration_unit_seconds: String(row.duration_unit_seconds ?? 0),
      active: row.active !== false,
    });
    setPreview(null);
    setDialogOpen(true);
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const dur =
        Number(form.duration_unit_seconds) > 0
          ? Number(form.duration_unit_seconds)
          : 8;
      const { data } = await api.post('/admin/billing/quote-preview', {
        media_type: form.media_type.trim().toLowerCase(),
        provider: form.provider.trim().toLowerCase(),
        mode: form.mode.trim().toLowerCase() || 'default',
        duration_seconds: dur,
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err, 'Preview failed'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const saveRate = async () => {
    setSaving(true);
    try {
      await api.put('/admin/billing/rates', {
        id: form.id || undefined,
        media_type: form.media_type.trim().toLowerCase(),
        provider: form.provider.trim().toLowerCase(),
        mode: form.mode.trim().toLowerCase() || 'default',
        label: form.label.trim(),
        credits_flat: parseFloat(form.credits_flat) || 0,
        credits_per_unit: parseFloat(form.credits_per_unit) || 0,
        duration_unit_seconds: parseFloat(form.duration_unit_seconds) || 0,
        active: form.active,
      });
      toast.success('Provider rate saved');
      setDialogOpen(false);
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteRate = async (rateId) => {
    if (!window.confirm('Delete this rate?')) return;
    try {
      await api.delete(`/admin/billing/rates/${rateId}`);
      toast.success('Rate deleted');
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err, 'Delete failed'));
    }
  };

  const cpi = Number(settings?.credits_per_inr) || Number(settingsDraft.credits_per_inr) || 10;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20'}>
      {!embedded && (
        <div className="mb-6">
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl flex items-center gap-2">
            <Coins className="w-7 h-7 text-indigo-500" />
            Billing & credits
          </h1>
          <p className="text-slate-500 mt-2 text-sm max-w-2xl">
            Set how many credits each AI provider charges per generation task, and adjust the global
            credit-to-rupee ratio for user wallets and recharges.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Select value={mediaFilter} onValueChange={setMediaFilter}>
            <SelectTrigger className="w-[180px] h-9 rounded-lg">
              <SelectValue placeholder="Filter media" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All media types</SelectItem>
              {MEDIA_TYPES.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full" onClick={loadAll}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button
            onClick={() => openCreate(mediaFilter === 'all' ? 'image' : mediaFilter)}
            className="rounded-full bg-indigo-500 text-white"
            size="sm"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add provider rate
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 mb-8">
        <div className="glass-panel rounded-2xl p-5 border border-slate-100">
          <h2 className="font-semibold text-lg flex items-center gap-2 mb-1">
            <IndianRupee className="w-5 h-5 text-emerald-600" />
            Global pricing (PR)
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Controls recharge conversion and how ₹ amounts display next to credit estimates.
          </p>
          <div className="space-y-4">
            <div>
              <Label>Credits per ₹1 (INR)</Label>
              <Input
                type="number"
                step="0.1"
                min="0.01"
                value={settingsDraft.credits_per_inr}
                onChange={(e) =>
                  setSettingsDraft((d) => ({ ...d, credits_per_inr: e.target.value }))
                }
                className="mt-1 max-w-[200px]"
              />
              <p className="text-xs text-slate-400 mt-1">
                Example: ₹100 recharge →{' '}
                {(parseFloat(settingsDraft.credits_per_inr) || 0) * 100} credits
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={settingsDraft.billing_enabled}
                onCheckedChange={(v) =>
                  setSettingsDraft((d) => ({ ...d, billing_enabled: v }))
                }
              />
              <Label>Billing enabled (deduct credits on generation)</Label>
            </div>
            {settings?.source && (
              <Badge variant="outline" className="text-xs">
                Source: {settings.source}
              </Badge>
            )}
            <Button
              onClick={saveSettings}
              disabled={savingSettings}
              className="rounded-full bg-emerald-600 text-white"
            >
              {savingSettings ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Save global pricing'
              )}
            </Button>
          </div>
        </div>

        <div className="glass-panel rounded-2xl p-5 border border-slate-100">
          <h2 className="font-semibold text-lg flex items-center gap-2 mb-1">
            <BarChart3 className="w-5 h-5 text-violet-600" />
            Credits consumed (by provider)
          </h2>
          <p className="text-xs text-slate-500 mb-3">From completed, charged generation jobs.</p>
          {usage.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">No charged jobs yet.</p>
          ) : (
            <div className="max-h-[220px] overflow-y-auto rounded-xl border border-slate-100">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Media</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead className="text-right">Jobs</TableHead>
                    <TableHead className="text-right">Credits</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usage.map((u) => (
                    <TableRow key={`${u.media_type}-${u.provider}`}>
                      <TableCell className="font-mono text-xs">{u.media_type}</TableCell>
                      <TableCell className="font-mono text-xs">{u.provider}</TableCell>
                      <TableCell className="text-right">{u.job_count}</TableCell>
                      <TableCell className="text-right font-medium">
                        {u.total_credits}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-5 border border-slate-100 mb-8">
        <h2 className="font-semibold text-lg flex items-center gap-2 mb-1">
          <Coins className="w-5 h-5 text-indigo-500" />
          Credit purchase plans
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Set original price, offer price, and credits granted per plan. Offer price defaults to half of
          original when seeding — adjust anytime. Users pay the offer price via Razorpay.
        </p>
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Original (₹)</TableHead>
                <TableHead>Offer (₹)</TableHead>
                <TableHead>Credits</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Active</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {creditPlans.map((plan) => {
                const draft = planDrafts[plan.id] || EMPTY_PLAN_FORM;
                const original = parseFloat(draft.original_price_inr) || 0;
                const offer = parseFloat(draft.offer_price_inr) || 0;
                const discount =
                  original > 0 ? Math.round((1 - offer / original) * 100) : plan.discount_percent || 0;
                return (
                  <TableRow key={plan.id}>
                    <TableCell className="font-semibold">{PLAN_LABELS[plan.id] || plan.id}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={draft.original_price_inr}
                        onChange={(e) =>
                          setPlanDrafts((prev) => ({
                            ...prev,
                            [plan.id]: { ...draft, original_price_inr: e.target.value },
                          }))
                        }
                        className="h-8 w-24"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min="1"
                          step="0.01"
                          value={draft.offer_price_inr}
                          onChange={(e) =>
                            setPlanDrafts((prev) => ({
                              ...prev,
                              [plan.id]: { ...draft, offer_price_inr: e.target.value },
                            }))
                          }
                          className="h-8 w-24"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 text-[10px] px-2"
                          onClick={() => setPlanHalfOffer(plan.id)}
                        >
                          50%
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={draft.credits}
                        onChange={(e) =>
                          setPlanDrafts((prev) => ({
                            ...prev,
                            [plan.id]: { ...draft, credits: e.target.value },
                          }))
                        }
                        className="h-8 w-28"
                      />
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">{discount}%</TableCell>
                    <TableCell>
                      <Switch
                        checked={draft.active}
                        onCheckedChange={(v) =>
                          setPlanDrafts((prev) => ({
                            ...prev,
                            [plan.id]: { ...draft, active: v },
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        className="h-8 rounded-full bg-indigo-500 text-white"
                        disabled={savingPlanId === plan.id}
                        onClick={() => saveCreditPlan(plan.id)}
                      >
                        {savingPlanId === plan.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="space-y-6">
        {groupedRates.map((group) => (
          <div
            key={group.value}
            className="bg-white rounded-2xl border border-slate-100 overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50/80 border-b">
              <h3 className="font-semibold text-sm">{group.label}</h3>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => openCreate(group.value)}
              >
                <Plus className="w-3 h-3 mr-1" />
                Add
              </Button>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Provider</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead>Charge</TableHead>
                  <TableHead>≈ ₹ (8s)</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.rows.map((row) => {
                  const sampleCr =
                    Number(row.credits_flat) ||
                    (Number(row.credits_per_unit) && Number(row.duration_unit_seconds)
                      ? Number(row.credits_per_unit) *
                        Math.ceil(8 / Number(row.duration_unit_seconds))
                      : 0);
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs font-medium">
                        {row.provider}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">
                        {row.mode}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-sm">
                        {row.label}
                      </TableCell>
                      <TableCell className="text-sm">{rateSummary(row)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatInr(sampleCr, cpi)}
                      </TableCell>
                      <TableCell>{row.active ? 'Yes' : 'No'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(row)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteRate(row.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ))}
        {groupedRates.length === 0 && (
          <p className="text-center text-slate-400 py-8 text-sm">No rates for this filter.</p>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Edit provider rate' : 'New provider rate'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Media type</Label>
                <Select
                  value={form.media_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, media_type: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIA_TYPES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Provider</Label>
                <Input
                  list={`providers-${form.media_type}`}
                  value={form.provider}
                  onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))}
                  className="mt-1 font-mono text-sm"
                  autoComplete="off"
                  name="billing-provider"
                />
                <datalist id={`providers-${form.media_type}`}>
                  {(PROVIDER_HINTS[form.media_type] || []).map((p) => (
                    <option key={p} value={p} />
                  ))}
                </datalist>
              </div>
            </div>
            <div>
              <Label>Mode</Label>
              <Input
                value={form.mode}
                onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))}
                placeholder="default, image-to-video, extend…"
                className="mt-1 font-mono text-sm"
              />
            </div>
            <div>
              <Label>Display label</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Flat credits</Label>
                <Input
                  type="number"
                  value={form.credits_flat}
                  onChange={(e) => setForm((f) => ({ ...f, credits_flat: e.target.value }))}
                  className="mt-1"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Per image / flat job</p>
              </div>
              <div>
                <Label>Per unit</Label>
                <Input
                  type="number"
                  value={form.credits_per_unit}
                  onChange={(e) => setForm((f) => ({ ...f, credits_per_unit: e.target.value }))}
                  className="mt-1"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">Video / audio blocks</p>
              </div>
              <div>
                <Label>Unit (sec)</Label>
                <Input
                  type="number"
                  value={form.duration_unit_seconds}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, duration_unit_seconds: e.target.value }))
                  }
                  className="mt-1"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">e.g. 5 or 30</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
              />
              <Label>Active</Label>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-slate-600">Preview charge</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={runPreview}
                  disabled={previewLoading}
                >
                  {previewLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Calculate'}
                </Button>
              </div>
              {preview && (
                <p className="text-sm mt-2">
                  <strong>{Number(preview.credits).toFixed(2)}</strong> credits
                  {preview.cost_inr != null && (
                    <span className="text-slate-500"> (≈ ₹{preview.cost_inr})</span>
                  )}
                  {preview.rate?.label && (
                    <span className="block text-xs text-slate-400 mt-1">{preview.rate.label}</span>
                  )}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveRate} disabled={saving} className="bg-indigo-500 text-white">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save rate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
